import { buildApiUrl } from '../config/environment';
import { Language } from '../config/translations';

// Types for settings
export interface UserSettings {
  restaurant_radius_km: number;
  supercharger_radius_km: number;
  tesla_supercharger: boolean;
  ionity: boolean;
  okq8: boolean;
  unit_system?: 'metric' | 'imperial';
  currency?: 'SEK' | 'USD' | 'EUR' | 'CHF' | 'DKK' | 'NOK';
}

export interface SubscriptionInfo {
  [key: string]: any;
}

export interface PricingPlan {
  [key: string]: any;
}

// Settings service functions
export const settingsService = {
  // Load user language preference
  loadUserLanguage: async (isAuthenticated: boolean, user: any): Promise<Language | null> => {
    if (isAuthenticated && user) {
      try {
        const response = await fetch(buildApiUrl('/api/settings/language'), {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          return data.language;
        }
      } catch (error) {
        console.error('Error loading user language:', error);
      }
    }
    return null;
  },

  // Save user language preference
  saveUserLanguage: async (newLanguage: Language, isAuthenticated: boolean, user: any): Promise<boolean> => {
    if (isAuthenticated && user) {
      try {
        const response = await fetch(buildApiUrl('/api/settings/language'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ language: newLanguage })
        });
        if (response.ok) {
          console.log(`Language preference saved: ${newLanguage}`);
          return true;
        }
      } catch (error) {
        console.error('Error saving user language:', error);
      }
    }
    return false;
  },

  // Load user settings
  loadSettings: async (isAuthenticated: boolean): Promise<UserSettings | null> => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings'), {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userSettings = await response.json();
        return {
          restaurant_radius_km: userSettings.restaurant_radius_km || 1.0,
          supercharger_radius_km: userSettings.supercharger_radius_km || 100.0,
          tesla_supercharger: userSettings.tesla_supercharger || true,
          ionity: userSettings.ionity || false,
          okq8: userSettings.okq8 || false,
          unit_system: userSettings.unit_system || 'metric',
          currency: userSettings.currency || 'USD'
        };
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
    return null;
  },

  // Save user settings
  saveSettings: async (settings: UserSettings): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(buildApiUrl('/api/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        return { success: true };
      } else {
        const data = await response.json();
        return { success: false, error: data.error || 'Failed to save settings' };
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      return { success: false, error: 'Failed to save settings' };
    }
  },

  // Load subscription info
  loadSubscriptionInfo: async (isAuthenticated: boolean): Promise<SubscriptionInfo | null> => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await fetch(buildApiUrl('/api/subscription'), {
        credentials: 'include'
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error loading subscription info:', error);
    }
    return null;
  },

  // Load pricing plans
  loadPricingPlans: async (currency?: string): Promise<{plans: PricingPlan[], currency: string} | null> => {
    try {
      const url = currency ? `/api/pricing?currency=${currency}` : '/api/pricing';
      const response = await fetch(buildApiUrl(url), {
        credentials: 'include' // Include session for user currency preference
      });
      
      if (response.ok) {
        const data = await response.json();
        return {
          plans: data.plans || [],
          currency: data.currency || 'USD'
        };
      }
    } catch (error) {
      console.error('Error loading pricing plans:', error);
    }
    return null;
  },

  // Load price range setting
  loadPriceRange: async (isAuthenticated: boolean): Promise<string | null> => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings/price-range'), {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.priceRange || '1,2,3';
      }
    } catch (error) {
      console.error('Error loading price range:', error);
    }
    return null;
  },

  // Update price range setting
  updatePriceRange: async (newPriceRange: string, isAuthenticated: boolean): Promise<boolean> => {
    if (!isAuthenticated) return false;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings/price-range'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ priceRange: newPriceRange })
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error updating price range:', error);
      return false;
    }
  },

  // Load max restaurants setting
  loadMaxRestaurants: async (isAuthenticated: boolean): Promise<number | null> => {
    if (!isAuthenticated) return null;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings/max-restaurants'), {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.maxRestaurants || 10;
      }
    } catch (error) {
      console.error('Error loading max restaurants:', error);
    }
    return null;
  },

  // Update max restaurants setting
  updateMaxRestaurants: async (newMaxRestaurants: number, isAuthenticated: boolean): Promise<boolean> => {
    if (!isAuthenticated) return false;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings/max-restaurants'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ maxRestaurants: newMaxRestaurants })
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error updating max restaurants:', error);
      return false;
    }
  },

  // Save user unit system preference
  saveUnitSystem: async (unitSystem: 'metric' | 'imperial', isAuthenticated: boolean): Promise<boolean> => {
    if (!isAuthenticated) return false;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings/unit-system'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ unitSystem })
      });
      if (response.ok) {
        console.log(`Unit system preference saved: ${unitSystem}`);
        return true;
      }
    } catch (error) {
      console.error('Error saving unit system:', error);
    }
    return false;
  },

  // Save user currency preference
  saveCurrency: async (currency: 'USD' | 'EUR' | 'SEK' | 'DKK' | 'NOK' | 'CHF', isAuthenticated: boolean): Promise<boolean> => {
    if (!isAuthenticated) return false;
    
    try {
      const response = await fetch(buildApiUrl('/api/settings/currency'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ currency })
      });
      if (response.ok) {
        console.log(`Currency preference saved: ${currency}`);
        return true;
      }
    } catch (error) {
      console.error('Error saving currency:', error);
    }
    return false;
  }
};